import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Paper,
  Chip,
} from '@mui/material';
import {
  Factory,
  ShoppingCart,
  AccountBalance,
  LocalHospital,
  Computer,
  CheckCircle,
  TrendingUp,
  Security,
  Analytics,
  Cloud,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import industryExpertiseData from '../data/industries/industryExpertiseData.json';
import industryStatsData from '../data/industries/industryStatsData.json';
import citStackData from '../data/industries/citStackData.json';

const Industries = () => {

  const industryExpertiseDataIconMap = {
    Factory: <Factory sx={{ fontSize: 50 }} />,
    ShoppingCart: <ShoppingCart sx={{ fontSize: 50 }} />,
    AccountBalance: <AccountBalance sx={{ fontSize: 50 }} />,
    LocalHospital: <LocalHospital sx={{ fontSize: 50 }} />,
    Computer: <Computer sx={{ fontSize: 50 }} />
  };

  const citStackDataIconMap = {
    Analytics: <Analytics sx={{ fontSize: 40 }} />,
    TrendingUp: <TrendingUp sx={{ fontSize: 40 }} />,
    Cloud: <Cloud sx={{ fontSize: 40 }} />,
    Security: <Security sx={{ fontSize: 40 }} />
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              Industries We Serve
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
              }}
            >
              Specialized Solutions Across Key Industry Verticals
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Industry Stats */}
      <Box sx={{ bgcolor: 'grey.50', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} textAlign="center">
            {industryStatsData.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Typography
                    variant="h3"
                    component="div"
                    color="primary"
                    fontWeight="bold"
                  >
                    {stat.number}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {stat.label}
                  </Typography>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Industries Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Our Industry Expertise
        </Typography>
        <Grid container spacing={4}>
          {industryExpertiseData.map((industry, index) => (
            <Grid item xs={12} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={4} alignItems="center">
                      <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                        <Avatar
                          sx={{
                            bgcolor: industry.color,
                            width: 100,
                            height: 100,
                            mx: 'auto',
                            mb: 2,
                          }}
                        >
                          {industryExpertiseDataIconMap[industry.icon]}
                        </Avatar>
                        <Typography variant="h4" component="h3" fontWeight="bold">
                          {industry.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                          {industry.description}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={9}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="error">
                              Key Challenges
                            </Typography>
                            <List dense>
                              {industry.challenges.map((challenge, challengeIndex) => (
                                <ListItem key={challengeIndex} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 30 }}>
                                    <TrendingUp color="error" sx={{ fontSize: 18 }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={challenge}
                                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                              Our Solutions
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              {industry.solutions.map((solution, solutionIndex) => (
                                <Chip
                                  key={solutionIndex}
                                  label={solution}
                                  size="small"
                                  sx={{
                                    m: 0.3,
                                    bgcolor: industry.color,
                                    color: 'white',
                                    fontSize: '0.75rem'
                                  }}
                                />
                              ))}
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="success.main">
                              Key Benefits
                            </Typography>
                            <List dense>
                              {industry.benefits.map((benefit, benefitIndex) => (
                                <ListItem key={benefitIndex} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 30 }}>
                                    <CheckCircle color="success" sx={{ fontSize: 18 }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={benefit}
                                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Technology Stack */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            Cross-Industry Technology Stack
          </Typography>

          <Grid container spacing={4}>
            {citStackData.map((stack, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: stack.color,
                          width: 60,
                          height: 60,
                          mr: 2
                        }}
                      >
                        {citStackDataIconMap[stack.icon]}
                      </Avatar>
                      <Typography variant="h5" fontWeight="bold">
                        {stack.category}
                      </Typography>
                    </Box>

                    <Box>
                      {stack.technologies.map((tech, techIndex) => (
                        <Chip
                          key={techIndex}
                          label={tech}
                          size="small"
                          sx={{ m: 0.5, bgcolor: 'grey.100' }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Ready to Transform Your Industry?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 300 }}>
            Let us help you leverage technology to overcome industry-specific challenges
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Contact us today to discuss how our industry expertise can benefit your organization
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Industries;